// components/AdminUserAdmin.tsx

import CheckIcon from '@mui/icons-material/Check';
import HistoryIcon from '@mui/icons-material/History';
import { Box, CircularProgress, Typography } from '@mui/material';
import AdminUser from 'AdminUser';
import AdminUserSearchBar from 'AdminUserSearchBar';
import BackendService, { AdminUserUpdate } from 'BackendService';
import { Permissions } from 'Permission';
import { PermissionGate } from 'PermissionContext';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import useRecentlyViewed from '../../hooks/admin/useRecentlyViewed';
import MessageToast from '../MessageToast';
import { Palette } from 'Palette';
import { StyledButton, StyledButtonGroup } from '../styles/StyledButtons';
import { StyledInput } from '../styles/StyledInputs';
import StyledSection from '../styles/StyledSection';
import { FormContainer, StyledFieldRow, StyledHeading, determineNextTone } from '../styles/styles';
import RecentlyViewedSidebar from './RecentlyViewedSidebar';

interface Props {
  adminUserId?: string;
  tone?: keyof typeof Palette;
  onNavigateToAdminUser: (id: string) => void;
  onNavigateToAdminUserPermissions: (id: string) => void;
  navigate: (path: string) => void;
  trackView: (modelType: string, modelId: string) => void;
}

interface State {
  adminUser: AdminUser | null;
  loading: boolean;
  submitting: boolean;
  formFields: AdminUserUpdate;
  errors?: { [K in keyof State['formFields']]?: string };
  linkCopied?: boolean;
  generatingToken?: boolean;
  message?: { type: 'success' | 'error'; text: string };
}

interface WrapperProps {
  adminUserId?: string;
  tone?: keyof typeof Palette;
}

const AdminUserAdmin = (props: WrapperProps) => {
  const navigate = useNavigate();
  const { trackView } = useRecentlyViewed();

  return (
    <AdminUserAdminImpl
      tone={props.tone}
      adminUserId={props.adminUserId}
      onNavigateToAdminUser={(id: string) => navigate(`/adminUsers/${id}`)}
      onNavigateToAdminUserPermissions={(id: string) => navigate(`/adminUsers/${id}/permissions`)}
      navigate={navigate}
      trackView={trackView}
    />
  );
};

class AdminUserAdminImpl extends React.Component<Props, State> {
  static defaultProps = { tone: 'default' as keyof typeof Palette };

  private fieldRefs: {
    [K in keyof State['formFields']]?: React.RefObject<HTMLInputElement>;
  } = {
    firstName: React.createRef(),
    lastName: React.createRef(),
    email: React.createRef(),
    mobile: React.createRef(),
  };

  private copyTimeout?: ReturnType<typeof setTimeout>;

  constructor(props: Props) {
    super(props);
    this.state = {
      adminUser: null,
      loading: true,
      submitting: false,
      formFields: {},
    };
  }

  componentDidMount() {
    const { adminUserId } = this.props;
    if (adminUserId) {
      this.loadAdminUser(adminUserId);
    } else {
      this.setState({ loading: false });
    }
  }

  componentDidUpdate(_: Props, prevState: State) {
    if (!prevState.linkCopied && this.state.linkCopied) {
      this.copyTimeout = setTimeout(() => {
        this.setState({ linkCopied: false });
      }, 1000);
    }
  }

  componentWillUnmount(): void {
    if (this.copyTimeout) clearTimeout(this.copyTimeout);
  }

  private loadAdminUser(id: string) {
    new BackendService()
      .getAdminUser(id)
      .then((adminUser: AdminUser) => {
        this.setState({
          adminUser,
          formFields: {
            firstName: adminUser.firstName || '',
            lastName: adminUser.lastName || '',
            email: adminUser.email || '',
            mobile: adminUser.mobile || '',
          },
        });

        // Track the admin user view for recently viewed feature
        this.props.trackView('ADMIN_USER', adminUser.id);
      })
      .finally(() => this.setState({ loading: false, generatingToken: false }));
  }

  private renderField(name: keyof AdminUserUpdate, label: string) {
    const { formFields, errors } = this.state;

    return (
      <StyledFieldRow key={name}>
        <StyledInput
          inputRef={this.fieldRefs[name]}
          label={label}
          value={formFields[name] || ''}
          error={!!errors?.[name]}
          helperText={errors?.[name]}
          onChange={(e) => {
            const value = e.target.value;
            this.setState((prev) => ({
              formFields: { ...prev.formFields, [name]: value },
              errors: { ...prev.errors, [name]: undefined },
            }));
          }}
          fullWidth
        />
      </StyledFieldRow>
    );
  }

  private updateAdminUser() {
    const { adminUser, formFields } = this.state;
    const svc = new BackendService();

    this.setState({ submitting: true });

    const action = adminUser ? 'update admin user' : 'create admin user';

    (adminUser ? svc.updateAdminUser(adminUser.id!, formFields) : svc.createAdminUser(formFields))
      .then((result: any) => {
        this.setState({ loading: true, adminUser: null });
        if (!adminUser) {
          this.props.onNavigateToAdminUser(result as string);
        } else {
          this.loadAdminUser(adminUser.id!);
        }
      })
      .catch((error) => {
        this.handleError(action, error);
      })
      .finally(() => this.setState({ submitting: false }));
  }

  private handleError(action: string, error: any) {
    const errorFields: State['errors'] = {};
    const message: { type: 'error' | 'success'; text: string } = {
      type: 'error',
      text: `Unable to ${action} due to an unexpected error, please contact support if problem persists`,
    };

    if (error?.missingFields) {
      error.missingFields.forEach(
        (field: keyof AdminUserUpdate) => (errorFields[field] = 'This field is required'),
      );
      message.text = `Unable to ${action} due to missing or invalid fields, see form for errors`;
    }

    if (error?.invalidFields) {
      Object.keys(error.invalidFields).forEach(
        (field) => (errorFields[field as keyof AdminUserUpdate] = error.invalidFields[field]),
      );
      message.text = `Unable to ${action} due to missing or invalid fields, see form for errors`;
    }

    this.setState({ errors: errorFields });
    this.setMessage(message);
  }

  private setMessage(message: { type: 'error' | 'success'; text: string }) {
    this.setState({ message });
    setTimeout(() => {
      this.setState({ message: undefined });
    }, 5000);
  }

  private generateToken() {
    this.setState({ generatingToken: true });

    new BackendService()
      .generateAdminInvitationToken(this.state.adminUser!.id!)
      .then(() => this.loadAdminUser(this.props.adminUserId!))
      .catch((error) => {
        this.setState({ generatingToken: false });
        this.handleError('generate invitation', error);
      });
  }

  private regenerateToken() {
    this.setState({ generatingToken: true });

    new BackendService()
      .regenerateAdminInvitationToken(this.state.adminUser!.id!)
      .then(() => this.loadAdminUser(this.props.adminUserId!))
      .catch((error) => {
        this.setState({ generatingToken: false });
        this.handleError('generate invitation', error);
      });
  }

  render() {
    const { adminUser, loading, submitting } = this.state;
    const { tone } = this.props;

    if (loading) return <CircularProgress />;
    if (this.props.adminUserId && !adminUser) return <Typography>Admin User not found</Typography>;

    return (
      <>
        <FormContainer>
          <AdminUserSearchBar tone={tone ?? 'default'} />
          <StyledSection tone={tone}>
            <StyledHeading tone={tone}>{adminUser ? 'Edit' : 'Add'} Admin User</StyledHeading>

            {this.renderField('firstName', 'First Name')}
            {this.renderField('lastName', 'Last Name')}
            {this.renderField('email', 'Email')}
            {this.renderField('mobile', 'Mobile')}

            <PermissionGate permissionKey={Permissions.AdminUserCreation}>
              <StyledButtonGroup justifyContent="flex-start" sx={{ mb: '1.875rem' }}>
                <StyledButton
                  variant="contained"
                  onClick={() => this.updateAdminUser()}
                  disabled={submitting}
                >
                  {submitting ? 'Saving...' : 'Save Admin User'}
                </StyledButton>
                <StyledButton
                  variant="outlined"
                  onClick={() => {
                    const adminUserId = adminUser?.id;
                    if (adminUserId) {
                      this.props.onNavigateToAdminUserPermissions(adminUserId);
                    }
                  }}
                  disabled={submitting}
                >
                  Manage Permissions
                </StyledButton>
                {adminUser && (
                  <StyledButton
                    variant="outlined"
                    startIcon={<HistoryIcon />}
                    onClick={() => this.props.navigate(`/activityLogs/ADMIN_USER/${adminUser.id}`)}
                  >
                    Activity Log
                  </StyledButton>
                )}
              </StyledButtonGroup>
            </PermissionGate>

            {adminUser && (
              <PermissionGate permissionKey={Permissions.AdminUserCreation}>
                <StyledSection tone={determineNextTone(this.props.tone || 'default')}>
                  <StyledHeading tone={determineNextTone(this.props.tone || 'default')}>
                    Invitation
                  </StyledHeading>
                  {adminUser.invitationLink ? (
                    adminUser.invitationExpired ? (
                      <>
                        <Typography color="error">Invitation expired</Typography>
                        <StyledButton onClick={() => this.regenerateToken()}>
                          Regenerate Token
                        </StyledButton>
                      </>
                    ) : (
                      <>
                        <StyledFieldRow>
                          <Typography>
                            Invitation link: {adminUser.invitationLink}
                            <Typography
                              component="span"
                              onClick={() => {
                                if (this.state.linkCopied) return;
                                navigator.clipboard.writeText(adminUser.invitationLink!);
                                this.setState({ linkCopied: true });
                              }}
                              sx={{
                                cursor: 'pointer',
                                color: this.state.linkCopied ? 'green' : 'primary.main',
                                textDecoration: this.state.linkCopied ? 'none' : 'underline',
                                ml: 1,
                              }}
                            >
                              {this.state.linkCopied ? <CheckIcon /> : 'Copy'}
                            </Typography>
                          </Typography>
                        </StyledFieldRow>

                        <StyledFieldRow>
                          <Typography>
                            Expires in {adminUser.invitationExpiredInDays} days
                          </Typography>
                        </StyledFieldRow>
                      </>
                    )
                  ) : (
                    <StyledButton onClick={() => this.generateToken()}>
                      Generate Invitation Link
                      {this.state.generatingToken && <CircularProgress size={20} />}
                    </StyledButton>
                  )}
                </StyledSection>
              </PermissionGate>
            )}
          </StyledSection>
        </FormContainer>

        {/* Recently Viewed Admin Users Sidebar */}
        <Box sx={{ mt: '1.5rem', mb: '1rem' }}>
          <RecentlyViewedSidebar modelType="ADMIN_USER" limit={5} compact={true} />
        </Box>
        {this.state.message && (
          <MessageToast
            type={this.state.message.type}
            message={this.state.message.text}
            open={true}
            onClose={() => this.setState({ message: undefined })}
          />
        )}
      </>
    );
  }
}

export default AdminUserAdmin;
